import { useEffect, useState } from 'react';
import { X, Download, Share, MoreVertical, PlusSquare } from 'lucide-react';
import { LogoMark } from './Logo';

const KEY = 'ssl-install-dismissed';
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);

export const InstallBanner = () => {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    const dismissed = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - dismissed < 7 * 24 * 3600 * 1000) return;
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const t = setTimeout(() => setShow(true), 2500);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); setGuide(false); };
  const install = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') dismiss(); else setPrompt(null);
      return;
    }
    setGuide(true);
  };

  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 sm:max-w-md sm:left-auto sm:right-4" data-testid="install-banner">
      <div className="card-elev rounded-2xl p-4 shadow-2xl shadow-sky-900/50 border border-sky-500/30">
        <div className="flex items-start gap-3">
          <LogoMark className="h-11 w-11 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold">Installer JOTALY</div>
            <div className="text-xs text-slate-400 mt-0.5">Ajoute l'icône sur ton écran d'accueil : lives plein écran et alertes instantanées.</div>
          </div>
          <button onClick={dismiss} data-testid="install-dismiss" aria-label="Fermer" className="text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        {!guide ? (
          <button onClick={install} data-testid="install-btn"
            className="mt-3 w-full btn-brand text-white font-bold py-2.5 rounded-xl inline-flex items-center justify-center gap-2">
            <Download className="h-4 w-4" />Installer l'application
          </button>
        ) : (
          <ol className="mt-3 space-y-2 text-sm text-slate-200" data-testid="install-guide">
            {isIOS() ? (
              <>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">1</span>Appuie sur <Share className="h-4 w-4 text-sky-400" /> <strong>Partager</strong> en bas de Safari</li>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">2</span>Choisis <PlusSquare className="h-4 w-4 text-sky-400" /> <strong>« Sur l'écran d'accueil »</strong></li>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">3</span>Appuie sur <strong>Ajouter</strong> — c'est prêt !</li>
              </>
            ) : (
              <>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">1</span>Appuie sur <MoreVertical className="h-4 w-4 text-sky-400" /> le menu de Chrome (en haut à droite)</li>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">2</span>Choisis <strong>« Installer l'application »</strong> ou « Ajouter à l'écran d'accueil »</li>
                <li className="flex items-center gap-2"><span className="h-6 w-6 rounded-full btn-brand text-white grid place-items-center text-xs font-bold">3</span>Confirme — l'icône apparaît sur ton téléphone</li>
              </>
            )}
          </ol>
        )}
      </div>
    </div>
  );
};
